<script setup lang="ts">
// Libraries: browse what's shared with you; create libraries in your own buckets and nodes (or
// a node someone opened to you), upload files and folders into them, and choose which spaces
// and people can see each one. Everything is end-to-end encrypted; the server never holds the
// files (HRTH-183).
import { deleteFiles } from '../lib/sealedStorage'
import { computed, markRaw, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { describeError } from '../api/http'
import type { LibraryKind, LibraryRef, LocalUser, PartyMedia, StorageLocation } from '../api/types'
import type { OpenLibrary } from '../sync/engine'
import {
  discardImport,
  fromDrop,
  fromInput,
  importFile,
  kvPendingStore,
  pendingFor,
  resumeImport,
  UploadCancelled,
  type PendingImport,
  type PendingStore,
  type PickedFile,
} from '../lib/libraryUpload'
import { browserCopies, COPY_MODES, type CopyMode } from '../lib/playbackCopy'
import { readLibrarySettings, sealLibrarySettings, type LibrarySettings } from '../lib/libraryEntries'
import { setStreamChoice, streamChoice, type StreamChoice } from '../lib/libraryPlay'
import { formatBytes } from '../lib/pattern'
import { filterPeople } from '../lib/people'
import { grantees, libraryPlaces, whereIs, type LibraryPlace, type OpenPlace } from '../lib/memberStorage'
import { askRoom, roomText } from '../lib/nodeShare'
import { useSettingsNav } from '../lib/settingsNav'
import { useSectionNav } from '../lib/sectionNav'
import SectionMenu from '../components/SectionMenu.vue'
import { usePulls } from '../stores/pulls'
import { useSession } from '../stores/session'
import ShareToChat from '../components/ShareToChat.vue'
import PromptModal from '../components/PromptModal.vue'
import EncryptedEntries from '../components/EncryptedEntries.vue'
import RepoBrowser from '../components/RepoBrowser.vue'
import CloneInstructions from '../components/CloneInstructions.vue'
import Icon from '../components/Icon.vue'
import Modal from '../components/Modal.vue'
import { cloneUrl as gitCloneUrl } from '../lib/git'
import { serverHost } from '../platform'

const session = useSession()
const pulls = usePulls()
const libraries = ref<OpenLibrary[]>([])
const active = ref<string | null>(null)
const query = ref('')
const sharing = ref<LibraryRef | null>(null)
const partying = ref<{ item: LibraryRef; queue: PartyMedia[] } | null>(null)
const users = ref<LocalUser[]>([])
const creating = ref(false)
const creatingRepo = ref(false)
/** Bumped to reload an encrypted library's files; whether it has none. */
const refreshTick = ref(0)
const noFiles = ref(true)
/** Where a new library can go (my own buckets and nodes, then nodes open to me), and the one picked. */
const locations = ref<LibraryPlace[]>([])
const newLocation = ref('')
/** Where to attach storage when there's none to pick. */
const noStorage = 'No storage yet: attach a bucket or add a node in Settings → Your storage.'
/** From a create dialog with nowhere to put the library: to Settings → Your storage. */
const settingsNav = useSettingsNav()
function attachStorage(): void {
  creating.value = creatingRepo.value = false
  settingsNav.open('storage')
}
/** I may create libraries: admins, and members when the server lets them have storage. */
const canCreate = computed(() => session.canLinkStorage)
const renaming = ref(false)
const deleting = ref(false)
/** The open library's settings (rename, delete, sharing, copies), behind the gear in its header. */
const libNav = useSectionNav<'about' | 'sharing' | 'copies'>('library-settings')
/** Uploading (files, a folder, copies) and this device's playback choice, behind icons beside the gear. */
const showingUpload = ref(false)
const showingStream = ref(false)

const lib = computed(() => libraries.value.find((l) => l.id === active.value) ?? null)
/** An admin looking after a member's library: they may unshare it or remove it from the server, nothing that needs its key. */
const moderating = computed(() => !!lib.value && !lib.value.can_manage && session.isAdmin && !!lib.value.owner)
/** The open library's settings: a menu of sections (lib/sectionNav.ts, ?library-settings=…). */
const libSections = computed(() => {
  const l = lib.value
  if (!l) return []
  return [
    {
      id: 'about' as const,
      label: 'About',
      hint: moderating.value ? `${l.owner}'s · remove from server` : 'Name, delete',
    },
    { id: 'sharing' as const, label: 'Sharing', hint: `${l.shared_with.length} spaces · ${l.shared_users.length} people` },
    ...(l.can_manage && l.kind === 'files'
      ? [{ id: 'copies' as const, label: 'Playback copies', hint: 'Lighter copies for streaming' }]
      : []),
  ]
})
const libShown = computed(() => libSections.value.find((s) => s.id === libNav.at.value) ?? null)
/** The storage location the open library lives in, shown read-only in About: mine, or the
 *  node someone opened to me (whoever can open About can see it). */
const libLocation = ref<StorageLocation | null>(null)
watch(
  () => [libShown.value?.id, lib.value?.location] as const,
  async ([section, id]) => {
    libLocation.value = null
    if (section !== 'about' || !id) return
    // In a sealed bucket: where it is comes from the library's own record, opened here.
    if (lib.value?.storage) {
      const rec = await session.engine?.libraryRead(lib.value)
      libLocation.value = rec
        ? {
            id,
            name: lib.value.owner === session.me ? 'Your bucket' : `${lib.value.owner ?? 'Someone'}'s bucket`,
            kind: 's3',
            configured: false,
            libraries: 0,
            attachments: 0,
            bucket: rec.bucket,
            prefix: rec.prefix,
            region: rec.region,
            ...(rec.endpoint ? { endpoint: rec.endpoint } : {}),
          }
        : null
      return
    }
    try {
      // In my own storage, or on someone's node open to me.
      const mine = await session.api.myStorage()
      const own = mine.locations.find((l) => l.id === id)
      const opened = mine.open_nodes?.find((l) => l.id === id)
      libLocation.value =
        (own?.sealed ? { ...own, name: (await session.engine?.bucket(own))?.name ?? (own.kind === 'node' ? 'Node' : 'Bucket') } : own) ??
        (opened
          ? {
              id: opened.id,
              name: `${session.profiles.get(opened.owner)?.display_name ?? opened.owner}'s node`,
              kind: 'node',
              configured: false,
              libraries: 0,
              attachments: 0,
            }
          : null)
    } catch {
      // Not fatal: About shows the location's id instead.
    }
  },
)
const libLeft = ref<string | null>(null)
watch(libShown, (now, was) => {
  if (!now && was) libLeft.value = was.id
})

// Search (SearchModal) opens a library at /library?lib=…, filtered with &q=… to a file.
const route = useRoute()
watch(
  () => [route.query.lib, route.query.q] as const,
  ([l, q]) => {
    if (typeof l === 'string' && l) active.value = l
    query.value = typeof q === 'string' ? q : ''
  },
  { immediate: true },
)
// Opening a library puts it in the address (?lib=…), like a channel: on phones the bare
// /library is the list, and Back returns to it. A new address drops the search's ?q=.
const router = useRouter()
const opened = computed(() => typeof route.query.lib === 'string' && !!route.query.lib)
function open(id: string): void {
  if (route.query.lib !== id || route.query.q) void router.push({ name: 'library', query: { lib: id } })
}
watch(active, () => (showingUpload.value = showingStream.value = false))
// Repositories are pushed to with git, not uploaded to.
const canUpload = computed(() => !!lib.value?.can_upload && lib.value.kind !== 'git' && !lib.value.node_wrap)
const localSpaces = computed(() => session.spaces.filter((s) => !s.host))
// Admins pick from every account; a member who owns a library, from the people they know.
const people = computed(() => (session.isAdmin ? users.value : grantees(session.profiles.values(), session.me)))
const otherUsers = computed(() => people.value.filter((u) => u.user_id !== session.me))
const peopleQuery = ref('')
const shownUsers = computed(() => filterPeople(otherUsers.value, peopleQuery.value))

const pad = (n: number, w = 3) => String(n).padStart(w, '0')

async function loadLibraries(): Promise<void> {
  try {
    libraries.value = await session.libraries()
    if (!active.value || !libraries.value.some((l) => l.id === active.value)) {
      active.value = libraries.value[0]?.id ?? null
    }
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function loadUsers(): Promise<void> {
  if (!session.isAdmin) return
  try {
    users.value = await session.api.localUsers()
  } catch {
    // Not fatal: per-user sharing just isn't offered.
  }
}

async function guarded(fn: () => Promise<unknown>): Promise<void> {
  try {
    await fn()
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

/**
 * Someone lost access to my library in my own bucket: they may keep its keys (read, or upload)
 * until I replace them at the provider (docs/STORAGE.md §11.2), so Sharing offers that.
 */
const removedFromBucket = ref(false)
watch(
  () => lib.value?.id,
  () => (removedFromBucket.value = false),
)
function noteRemoved(on: boolean): void {
  if (!on && lib.value?.storage && lib.value.owner === session.me) removedFromBucket.value = true
}

const toggleShare = (space: string, on: boolean) =>
  guarded(async () => {
    if (!lib.value) return
    if (on) await session.shareLibrary(lib.value.id, space)
    else await session.api.unshareLibrary(lib.value.id, space)
    noteRemoved(on)
    await loadLibraries()
  })

const toggleRole = (role: string, space: string, on: boolean) =>
  guarded(async () => {
    if (!lib.value) return
    if (on) await session.shareLibrary(lib.value.id, space, role)
    else await session.api.unshareLibraryWithRole(lib.value.id, role)
    noteRemoved(on)
    await loadLibraries()
  })

const toggleUser = (user: string, on: boolean) =>
  guarded(async () => {
    if (!lib.value) return
    if (on) await session.grantLibrary(lib.value.id, user)
    else await session.api.revokeLibrary(lib.value.id, user)
    noteRemoved(on)
    await loadLibraries()
  })

/** Upload for everyone it's shared with through a space or role (shared first). */
const toggleSpaceUpload = (space: string, on: boolean) =>
  guarded(async () => {
    if (!lib.value) return
    await session.api.setSpaceUpload(lib.value.id, space, on)
    noteRemoved(on)
    await loadLibraries()
  })
const toggleRoleUpload = (role: string, on: boolean) =>
  guarded(async () => {
    if (!lib.value) return
    await session.api.setRoleUpload(lib.value.id, role, on)
    noteRemoved(on)
    await loadLibraries()
  })
/** What upload means here: pushing, for a git repository. */
const uploadWord = computed(() => (lib.value?.kind === 'git' ? 'Push' : 'Upload'))

/** Upload permission; granting it also grants access. */
const toggleUpload = (user: string, on: boolean) =>
  guarded(async () => {
    if (!lib.value) return
    if (on) await session.api.grantLibraryUpload(lib.value.id, user)
    else await session.api.revokeLibraryUpload(lib.value.id, user)
    noteRemoved(on)
    await loadLibraries()
  })

/** The command that clones a repository library (git-remote-hrth). */
const cloneUrl = computed(() =>
  lib.value?.kind === 'git' ? gitCloneUrl(serverHost(), lib.value.id, lib.value.locked ? null : lib.value.name) : '',
)

const createRepository = (name: string) => createLibrary(name, 'git')

/** On a node that isn't connected: as the server last said over the socket, else as listed. */
function nodeOffline(l: { location?: string; node_online?: boolean }): boolean {
  if (l.node_online === undefined) return false
  return !(session.nodeOnline[l.location ?? ''] ?? l.node_online)
}

/** Loads where a new library can live, before the create dialog opens. */
async function startCreating(kind: LibraryKind): Promise<void> {
  try {
    const mine = await session.api.myStorage()
    // Sealed buckets and nodes are named in their records, opened here. A node keeps what's
    // uploaded to it beside the folders it shares (HRTH-182).
    const named = await Promise.all(
      mine.locations.map(async (l) =>
        l.sealed ? { ...l, name: (await session.engine?.bucket(l))?.name ?? (l.kind === 'node' ? 'Node' : 'Bucket') } : l,
      ),
    )
    // Other people's nodes open to me, named by the node, where I have room.
    const opened: OpenPlace[] = []
    for (const n of mine.open_nodes ?? []) {
      if (!n.online) continue
      const a = await askRoom(session.api, n.id).catch(() => null)
      if (!a || a.allowance === 'none') continue
      opened.push({ id: n.id, name: a.name ?? 'Node', owner: session.profiles.get(n.owner)?.display_name ?? n.owner, room: roomText(a) })
    }
    locations.value = libraryPlaces(named, opened)
    newLocation.value = locations.value[0]?.id ?? ''
  } catch (e) {
    session.toast(describeError(e), 'error')
    return
  }
  if (kind === 'git') creatingRepo.value = true
  else creating.value = true
}

async function createLibrary(name: string, kind: LibraryKind = 'files'): Promise<void> {
  // Libraries live in their owner's bucket or node, or a node someone opened to them.
  if (!newLocation.value) throw new Error(noStorage)
  const l = await session.createLibrary(name, newLocation.value, kind)
  await loadLibraries()
  open(l.id)
}

async function renameLibrary(name: string): Promise<void> {
  if (!lib.value) return
  await session.renameLibrary(lib.value.id, name)
  await loadLibraries()
}

/** What deleting the open library loses, for the type-the-name dialog. */
const deleteMessage = computed(() => {
  const l = lib.value
  if (!l) return ''
  if (moderating.value) return `This removes “${l.name}” from this server for everyone. Its files stay in ${l.owner}'s storage.`
  const what = l.kind === 'git' ? 'its history, branches and pull requests' : `its ${l.items} files`
  return `This deletes “${l.name}” and ${what}. It can't be undone.`
})

async function deleteLibrary(): Promise<void> {
  const l = lib.value
  if (!l) return
  // A sealed bucket's files are deleted from here (the server can't reach it); an admin
  // removing someone else's library has no write key, so their files stay.
  if (l.storage_write && l.can_manage) {
    const { entries } = await session.api.libraryEntries(l.id)
    await deleteFiles(session.api, l.id, entries.flatMap((e) => e.blobs.map((b) => b.id)))
  }
  await session.api.deleteLibrary(l.id)
  // Closed here: the dialog unmounts with the library, before it could close itself,
  // and would otherwise reopen for the next library shown.
  deleting.value = false
  active.value = null
  await router.replace({ name: 'library' })
  await loadLibraries()
}

// --- Uploads -------------------------------------------------------------------------

interface Job {
  key: number
  library: string
  path: string
  size: number
  file: File
  progress: number
  /** Making a playback copy on this device, 0 to 1, before uploading. */
  converting?: number
  state: 'queued' | 'uploading' | 'done' | 'error' | 'cancelled'
  error?: string
  ac: AbortController
  /** An import cut short by a reload that this file carries on. */
  resume?: PendingImport
  /** When to make a playback copy. */
  copies: CopyMode
}
const jobs = ref<Job[]>([])

// Playback copies (HRTH-105.9): the library's default, under its key, set by admins; each
// upload can choose otherwise. Originals are always kept as they are.
const libSettings = ref<LibrarySettings>({})
const uploadCopies = ref<CopyMode>('auto')
const copyHint = (m: CopyMode) => COPY_MODES.find((c) => c.mode === m)?.hint
watch(
  () => [lib.value?.id, lib.value?.settings] as const,
  async ([id, sealed]) => {
    const s = id ? await readLibrarySettings(session.libraryKey(id), id, sealed) : {}
    if (id !== lib.value?.id) return
    libSettings.value = s
    uploadCopies.value = s.copies ?? 'auto'
  },
  { immediate: true },
)

const saveDefaultCopies = (copies: CopyMode) =>
  guarded(async () => {
    const l = lib.value
    const key = l && session.libraryKey(l.id)
    if (!l || !key) return
    await session.api.setLibrarySettings(l.id, await sealLibrarySettings(key, l.id, { ...libSettings.value, copies }))
    await loadLibraries()
  })

// Where a file has a lighter copy, which one this device streams.
const stream = ref<StreamChoice>(streamChoice())
const STREAM_OPTIONS: { value: StreamChoice; label: string; hint: string }[] = [
  { value: 'auto', label: 'Automatic', hint: 'The lighter copy on phones and data saver, the original elsewhere' },
  { value: 'original', label: 'Original', hint: 'Always the file as uploaded: best quality, most data' },
  { value: 'lighter', label: 'Lighter copy', hint: 'A smaller copy where a file has one: less data' },
]
watch(stream, setStreamChoice)
const dragging = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const folderInput = ref<HTMLInputElement | null>(null)
const PARALLEL = 2
let running = 0
let seq = 0
let refreshTimer: ReturnType<typeof setTimeout> | null = null

const pending = computed(() => jobs.value.filter((j) => j.state === 'queued' || j.state === 'uploading'))
const totals = computed(() => {
  const all = jobs.value.filter((j) => j.state !== 'cancelled')
  const size = all.reduce((n, j) => n + j.size, 0)
  const sent = all.reduce((n, j) => n + j.size * j.progress, 0)
  return { size, fraction: size ? sent / size : 0, done: all.filter((j) => j.state === 'done').length, count: all.length }
})

// Imports cut short by a reload are remembered on this device; picking the same files
// again carries them on (HRTH-105.4).
let pendingStore: { handle: unknown; store: PendingStore } | null = null
function pendingImports(): PendingStore | null {
  const handle = session.storageHandle()
  if (!handle) return null
  if (pendingStore?.handle !== handle) pendingStore = { handle, store: kvPendingStore(handle) }
  return pendingStore.store
}
const interrupted = ref<PendingImport[]>([])

async function loadInterrupted(): Promise<void> {
  const store = pendingImports()
  const l = lib.value
  if (!store || !l) return void (interrupted.value = [])
  const all = await store.list()
  // Playback copies kept for imports nothing will resume.
  void browserCopies.prune(all.flatMap((p) => (p.copy?.name ? [p.copy.name] : []))).catch(() => undefined)
  const mine = all.filter((p) => p.library === l.id)
  if (mine.length) {
    // Entries deleted meanwhile have nothing left to resume.
    const live = new Set((await session.api.libraryEntries(l.id)).entries.map((e) => e.id))
    for (const p of mine.filter((p) => !live.has(p.entry))) await store.remove(p.entry)
  }
  const busy = jobs.value.filter((j) => j.state === 'queued' || j.state === 'uploading')
  interrupted.value = (await store.list()).filter(
    (p) => p.library === l.id && !busy.some((j) => j.resume?.entry === p.entry || (j.path === p.path && j.size === p.size)),
  )
}

const discardInterrupted = () =>
  guarded(async () => {
    const store = pendingImports()
    if (!store || !confirm('Discard the interrupted uploads? What they uploaded so far is deleted.')) return
    for (const p of interrupted.value) await discardImport(session.api, p, store)
    await loadInterrupted()
    scheduleRefresh()
  })

function enqueue(picked: { files: PickedFile[]; skipped: number }): void {
  if (!lib.value) return
  for (const p of picked.files) {
    const resume = pendingFor(interrupted.value, lib.value.id, p)
    jobs.value.push({
      ...(resume ? { resume } : {}),
      key: ++seq,
      library: lib.value.id,
      path: p.path,
      size: p.file.size,
      file: markRaw(p.file),
      progress: 0,
      copies: uploadCopies.value,
      state: 'queued',
      ac: markRaw(new AbortController()),
    })
  }
  if (picked.skipped) session.toast(`Skipped ${picked.skipped} hidden file${picked.skipped === 1 ? '' : 's'}`)
  void loadInterrupted()
  pump()
}

function pump(): void {
  while (running < PARALLEL) {
    const j = jobs.value.find((x) => x.state === 'queued')
    if (!j) return
    running++
    void run(j).finally(() => {
      running--
      pump()
    })
  }
}

async function run(j: Job): Promise<void> {
  j.state = 'uploading'
  try {
    const key = session.libraryKey(j.library)
    if (!key) throw new Error('This device doesn’t have this library’s key yet')
    const opts = {
      signal: j.ac.signal,
      onProgress: (f: number) => {
        j.converting = undefined
        j.progress = f
      },
      onConvert: (f: number) => (j.converting = f),
      pending: pendingImports() ?? undefined,
      libraryKey: key,
      copies: j.copies,
    }
    if (j.resume) await resumeImport(session.api, j.resume, j.file, opts)
    else await importFile(session.api, j.library, key, { file: j.file, path: j.path }, opts)
    j.progress = 1
    j.state = 'done'
    scheduleRefresh()
  } catch (e) {
    if (e instanceof UploadCancelled) j.state = 'cancelled'
    else {
      j.state = 'error'
      j.error = describeError(e)
    }
  }
  void loadInterrupted()
}

function cancel(j: Job): void {
  if (j.state === 'queued') j.state = 'cancelled'
  else j.ac.abort()
}

function clearFinished(): void {
  jobs.value = jobs.value.filter((j) => j.state === 'queued' || j.state === 'uploading')
}

function scheduleRefresh(): void {
  if (refreshTimer) clearTimeout(refreshTimer)
  refreshTimer = setTimeout(() => {
    refreshTick.value++
    void loadLibraries()
  }, 400)
}

function onPick(input: HTMLInputElement): void {
  if (input.files) enqueue(fromInput(input.files))
  input.value = ''
  showingUpload.value = false
}

async function onDrop(e: DragEvent): Promise<void> {
  dragging.value = false
  if (!canUpload.value || !e.dataTransfer) return
  enqueue(await fromDrop(e.dataTransfer))
}

onMounted(() => void loadLibraries())
// Admin status arrives after the session loads: on a reload straight into this page it
// isn't known yet at mount, so load the account list whenever it turns true.
watch(
  () => session.isAdmin,
  (admin) => admin && void loadUsers(),
  { immediate: true },
)
watch(() => lib.value?.id, () => void loadInterrupted())
</script>

<template>
  <main class="flex min-w-0 flex-1 overflow-hidden bg-bg-tertiary max-md:flex-col" data-testid="library">
    <!-- Libraries -->
    <!-- Phones show the list or an open library, like channels; wide screens both -->
    <aside class="flex shrink-0 flex-col border-border bg-bg-secondary max-md:flex-1 md:w-64 md:border-r" :class="opened ? 'max-md:hidden' : ''">
      <div class="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-4 max-md:h-16">
        <span class="min-w-0 flex-1">
          <span class="block text-[15px] font-medium">Library</span>
          <span class="mono block text-text-muted">{{ pad(libraries.length, 2) }} collections</span>
        </span>
        <button v-if="canCreate" class="btn-icon" title="New library" aria-label="New library" data-testid="new-library" @click="startCreating('files')">
          <Icon name="plus" />
        </button>
        <button v-if="canCreate" class="btn-icon" title="New git repository" data-testid="new-repository" @click="startCreating('git')">git</button>
      </div>
      <nav class="scroll-thin flex flex-1 flex-col overflow-auto">
        <button
          v-for="l in libraries"
          :key="l.id"
          class="relative shrink-0 px-4 py-2 text-left transition-colors max-md:py-3"
          :class="l.id === active ? 'bg-bg-hover text-text-primary' : 'text-text-muted hover:text-text-primary'"
          :data-testid="`library-tab-${l.name}`"
          @click="open(l.id)"
        >
          <span v-if="l.id === active" class="absolute inset-y-0 left-0 w-0.5 bg-ink" />
          <span class="flex items-center gap-2">
            <span class="block min-w-0 flex-1 truncate text-sm">{{ l.name }}</span>
            <span
              v-if="pulls.byLibrary[l.id]"
              class="badge"
              :title="`${pulls.byLibrary[l.id]} pull request${pulls.byLibrary[l.id] === 1 ? '' : 's'} with news`"
              :data-testid="`repo-unread-${l.name}`"
            >{{ pulls.byLibrary[l.id] }}</span>
          </span>
          <span class="mono block truncate text-text-disabled">
            <template v-if="nodeOffline(l)"><span class="text-signal" :data-testid="`library-offline-${l.name}`">offline</span> · </template><template v-if="l.kind === 'git'">repository · </template>{{ pad(l.items) }} · {{ formatBytes(l.bytes) }}<template v-if="l.owner"> · {{ l.owner === session.me ? 'yours' : l.owner }}</template>
          </span>
        </button>
      </nav>
    </aside>

    <section
      class="scroll-thin relative min-w-0 flex-1 overflow-y-auto pb-28"
      :class="opened || libraries.length === 0 ? '' : 'max-md:hidden'"
      @dragover.prevent="canUpload && (dragging = true)"
      @dragleave.self="dragging = false"
      @drop.prevent="onDrop"
    >
      <!-- Nothing yet -->
      <div v-if="libraries.length === 0" class="field-grid flex h-full items-center justify-center p-6" data-testid="library-empty">
        <div class="max-w-sm border border-border bg-bg-primary p-8">
          <div class="mono mb-6 text-text-muted">000 ITEMS · 00 COLLECTIONS</div>
          <template v-if="canCreate">
            <h2 class="mb-2 text-2xl font-light tracking-tight">Start a library</h2>
            <p class="mb-6 text-sm text-text-muted">
              Create a library, drop music and video into it, then choose which spaces and people can stream it.
            </p>
            <button class="btn-primary" data-testid="create-first-library" @click="startCreating('files')">New library</button>
          </template>
          <template v-else>
            <h2 class="mb-2 text-2xl font-light tracking-tight">Nothing shared yet</h2>
            <p class="text-sm text-text-muted">No media libraries are shared with you or your spaces yet.</p>
          </template>
        </div>
      </div>

      <template v-else-if="lib">
        <!-- Header: name and readout -->
        <header class="border-b border-border px-6 pt-6 max-md:px-4 max-md:pt-3">
          <button class="-ml-3 mb-1 flex h-10 items-center gap-1 pr-2 text-text-secondary hover:text-text-primary md:hidden" data-testid="library-back" @click="router.push({ name: 'library' })">
            <svg viewBox="0 0 16 16" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M10 3L5 8l5 5" />
            </svg>
            <span class="mono uppercase">Library</span>
          </button>
          <!-- Name and what it holds; then search and the library's controls -->
          <div class="flex flex-wrap items-baseline gap-x-4 gap-y-1 pb-3">
            <h1 class="min-w-0 truncate text-4xl font-light tracking-tight max-md:text-3xl" data-testid="library-name">{{ lib.name }}</h1>
            <p v-if="lib.kind !== 'git'" class="mono text-text-muted uppercase" data-testid="library-counts">
              {{ pad(lib.items) }} items · {{ formatBytes(lib.bytes) }}
            </p>
            <p v-if="nodeOffline(lib)" class="mono text-signal" data-testid="library-node-offline">
              Its node is offline: files play again when it's back
            </p>
          </div>
          <div class="flex items-center gap-2 pb-4">
            <input
              v-if="lib.kind !== 'git'"
              v-model="query"
              type="search"
              class="input min-w-0 flex-1 md:max-w-96 max-md:h-11"
              placeholder="Search"
              aria-label="Search the library"
              data-testid="library-search"
            />
            <span v-else class="flex-1" />
            <button v-if="lib.can_manage || moderating" class="btn-icon" title="Library settings" aria-label="Library settings" data-testid="library-settings" @click="libNav.open()">
              <Icon name="gear" />
            </button>
            <button v-if="canUpload" class="btn-icon" title="Upload" aria-label="Upload" data-testid="upload-open" @click="showingUpload = true">
              <Icon name="upload" />
            </button>
            <button
              v-if="lib.kind === 'files'"
              class="btn-icon"
              title="Playback on this device"
              aria-label="Playback on this device"
              data-testid="stream-open"
              @click="showingStream = true"
            >
              <Icon name="stream" />
            </button>
          </div>
          <CloneInstructions v-if="lib.kind === 'git'" :clone-url="cloneUrl" />
        </header>

        <!-- Library settings: rename or delete; who it's shared with; playback copies -->
        <Modal
          v-if="libNav.at.value && (lib.can_manage || moderating)"
          page
          :title="libShown ? `${lib.name} · ${libShown.label}` : 'Library settings'"
          :back="libShown ? 'Library settings' : undefined"
          @back="libNav.back()"
          @close="libNav.close()"
        >
          <SectionMenu v-if="!libShown" :sections="libSections" :from="libLeft" testid="library-section" @open="(id) => libNav.enter(id as 'about' | 'sharing' | 'copies')" />
          <div v-else class="-mx-5 -my-5" data-testid="library-settings-panel">
          <template v-if="libShown.id === 'about'">
          <p class="mono border-b border-border px-6 py-2.5 text-text-muted uppercase" data-testid="library-about">
            {{ lib.kind === 'git' ? 'Git repository' : lib.node_wrap ? 'Shared folder' : 'Library' }} · {{ lib.id }} · end-to-end encrypted<template v-if="lib.owner"> · {{ lib.owner === session.me ? 'yours' : `${lib.owner}'s` }}</template>
          </p>
          <div v-if="lib.location" class="border-b border-border px-6 py-3" data-testid="library-storage">
            <h2 class="label">Stored in</h2>
            <template v-if="libLocation">
              <p class="text-sm">
                {{ libLocation.name }}
                <span class="mono text-text-muted uppercase">· {{ libLocation.kind === 's3' ? 'S3 bucket' : 'node' }}<template v-if="libLocation.owner"> · {{ libLocation.owner === session.me ? 'yours' : `${libLocation.owner}'s` }}</template></span>
              </p>
              <p class="mono truncate text-text-muted" :title="whereIs(libLocation)">{{ whereIs(libLocation) }}</p>
            </template>
            <p v-else class="mono text-text-muted">{{ lib.location }}</p>
          </div>
          <p v-if="moderating" class="mono border-b border-border px-6 py-2.5 text-text-muted" data-testid="library-moderating">
            {{ lib.owner }} owns this library and runs it. As an admin you can take people off it or remove it from this
            server; its files stay in their storage.
          </p>
          <div class="flex flex-wrap items-center gap-2 border-b border-border px-6 py-3">
            <button v-if="!moderating" class="btn-ghost" data-testid="rename-library" @click="libNav.close(), (renaming = true)">Rename</button>
            <button class="btn-ghost text-error hover:border-signal hover:text-error" data-testid="delete-library" @click="libNav.close(), (deleting = true)">
              {{ moderating ? 'Remove from server' : 'Delete' }}
            </button>
          </div>
          <p v-if="lib.node_wrap" class="mono px-6 py-2.5 text-text-muted">
            A folder on your node: files appear as it finds them there. Deleting the library leaves the folder as it is.
          </p>
          </template>
          <div v-else-if="libShown.id === 'sharing'" class="grid">
            <p v-if="removedFromBucket" class="mono border-b border-border px-6 py-2.5 text-signal" data-testid="library-rekey-hint">
              People you removed may still have this bucket's keys, and could fetch its encrypted files straight from it.
              <button type="button" class="underline hover:text-text-primary" data-testid="library-rekey-open" @click="settingsNav.open('storage')">Replace its keys</button>
            </p>
            <div class="border-b border-border px-6 py-4">
              <h2 class="label">Spaces · {{ pad(lib.shared_with.length, 2) }}</h2>
              <p v-if="localSpaces.length === 0" class="mono text-text-muted">Create a space to share this library.</p>
              <template v-for="s in localSpaces" :key="s.id">
                <div class="flex items-center gap-3 py-1 text-sm">
                  <label class="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      :checked="lib.shared_with.includes(s.id)"
                      :disabled="moderating && !lib.shared_with.includes(s.id)"
                      :data-testid="`share-library-${s.name}`"
                      @change="toggleShare(s.id, ($event.target as HTMLInputElement).checked)"
                    />
                    <span class="flex-1 truncate">{{ s.name }}</span>
                    <span class="mono text-text-disabled">{{ pad(s.members.length, 2) }}</span>
                  </label>
                  <label
                    v-if="!lib.node_wrap"
                    class="mono flex items-center gap-1.5 text-text-muted uppercase"
                    :class="lib.shared_with.includes(s.id) ? 'cursor-pointer hover:text-text-primary' : 'opacity-40'"
                    :title="lib.shared_with.includes(s.id) ? `Everyone in ${s.name} may ${uploadWord.toLowerCase()}, and edit or delete only what they add` : 'Share it with the space first'"
                  >
                    <input
                      type="checkbox"
                      :checked="lib.upload_spaces?.includes(s.id) ?? false"
                      :disabled="!lib.shared_with.includes(s.id) || moderating"
                      :data-testid="`space-upload-${s.name}`"
                      @change="toggleSpaceUpload(s.id, ($event.target as HTMLInputElement).checked)"
                    />
                    {{ uploadWord }}
                  </label>
                </div>
                <!-- Or just the people holding one of its roles -->
                <div v-for="r in s.roles ?? []" :key="r.id" class="flex items-center gap-3 py-0.5 pl-7 text-sm">
                  <label
                    class="flex min-w-0 flex-1 items-center gap-3"
                    :class="lib.shared_with.includes(s.id) ? 'opacity-40' : 'cursor-pointer'"
                    :title="lib.shared_with.includes(s.id) ? 'The whole space already has access' : `Everyone in ${s.name} with the ${r.name} role`"
                  >
                    <input
                      type="checkbox"
                      :checked="lib.shared_with.includes(s.id) || lib.shared_roles.includes(r.id)"
                      :disabled="lib.shared_with.includes(s.id) || (moderating && !lib.shared_roles.includes(r.id))"
                      :data-testid="`share-role-${s.name}-${r.name}`"
                      @change="toggleRole(r.id, s.id, ($event.target as HTMLInputElement).checked)"
                    />
                    <span class="h-1.5 w-1.5 shrink-0" :style="{ background: r.color }" />
                    <span class="mono flex-1 truncate text-text-secondary uppercase">{{ r.name }}</span>
                    <span class="mono text-text-disabled">{{ pad(s.members.filter((m) => m.roles?.includes(r.id)).length, 2) }}</span>
                  </label>
                  <label
                    v-if="!lib.node_wrap && lib.shared_roles.includes(r.id) && !lib.shared_with.includes(s.id)"
                    class="mono flex cursor-pointer items-center gap-1.5 text-text-muted uppercase hover:text-text-primary"
                    :title="`Everyone with the ${r.name} role may ${uploadWord.toLowerCase()}, and edit or delete only what they add`"
                  >
                    <input
                      type="checkbox"
                      :checked="lib.upload_roles?.includes(r.id) ?? false"
                      :disabled="moderating"
                      :data-testid="`role-upload-${s.name}-${r.name}`"
                      @change="toggleRoleUpload(r.id, ($event.target as HTMLInputElement).checked)"
                    />
                    {{ uploadWord }}
                  </label>
                </div>
              </template>
            </div>
            <div class="px-6 py-4">
              <div class="mb-2 flex items-center gap-3">
                <h2 class="label mb-0 shrink-0">People · {{ pad(lib.shared_users.length, 2) }}</h2>
                <input
                  v-if="otherUsers.length"
                  v-model="peopleQuery"
                  type="search"
                  class="input h-7 min-w-0 flex-1 py-0 text-xs"
                  placeholder="Search user@host or name"
                  aria-label="Search people"
                  data-testid="people-search"
                />
              </div>
              <p v-if="otherUsers.length === 0" class="mono text-text-muted">
                {{ session.isAdmin ? 'No other accounts on this server yet.' : 'No one yet: people show up here once you share a space or a conversation.' }}
              </p>
              <p v-else-if="shownUsers.length === 0" class="mono py-1 text-text-muted" data-testid="people-empty">No one matches.</p>
              <div class="scroll-thin max-h-72 overflow-y-auto" data-testid="people-list">
              <div v-for="u in shownUsers" :key="u.user_id" class="flex items-center gap-3 py-1 text-sm">
                <label class="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    :checked="lib.shared_users.includes(u.user_id)"
                    :disabled="moderating && !lib.shared_users.includes(u.user_id)"
                    :data-testid="`grant-user-${u.display_name}`"
                    @change="toggleUser(u.user_id, ($event.target as HTMLInputElement).checked)"
                  />
                  <span class="min-w-0 flex-1 leading-tight">
                    <span class="block truncate" data-testid="grant-user-id">{{ u.user_id }}</span>
                    <span class="block truncate text-xs text-text-muted">
                      {{ u.display_name }}<span v-if="u.is_admin" class="mono ml-1.5 text-text-disabled uppercase">admin</span>
                    </span>
                  </span>
                </label>
                <label
                  v-if="!lib.node_wrap"
                  class="mono flex cursor-pointer items-center gap-1.5 text-text-muted uppercase hover:text-text-primary"
                  title="May upload, and edit or delete only what they upload"
                >
                  <input
                    type="checkbox"
                    :checked="lib.uploaders.includes(u.user_id)"
                    :disabled="moderating && !lib.uploaders.includes(u.user_id)"
                    :data-testid="`grant-upload-${u.display_name}`"
                    @change="toggleUpload(u.user_id, ($event.target as HTMLInputElement).checked)"
                  />
                  Upload
                </label>
              </div>
              </div>
            </div>
          </div>
          <div v-else class="flex flex-wrap items-center gap-3 px-6 py-4" data-testid="library-copies">
            <h2 class="label mb-0 shrink-0">Playback copies</h2>
            <select
              :value="libSettings.copies ?? 'auto'"
              class="input h-8 w-auto py-0 text-xs"
              data-testid="library-copies-default"
              @change="saveDefaultCopies(($event.target as HTMLSelectElement).value as CopyMode)"
            >
              <option v-for="m in COPY_MODES" :key="m.mode" :value="m.mode">{{ m.label }}</option>
            </select>
            <p class="mono min-w-0 basis-full text-text-muted" data-testid="library-copies-hint">
              Default for uploads here: {{ copyHint(libSettings.copies ?? 'auto')?.toLowerCase() }}. Originals are always kept as uploaded.
            </p>
          </div>
          </div>
        </Modal>

        <!-- Upload progress; starting one is behind the upload icon, managing the library behind the gear -->
        <input
          v-if="canUpload"
          ref="fileInput"
          type="file"
          multiple
          class="hidden"
          data-testid="upload-files"
          @change="onPick($event.target as HTMLInputElement)"
        />
        <input v-if="canUpload" ref="folderInput" type="file" webkitdirectory class="hidden" data-testid="upload-folder" @change="onPick($event.target as HTMLInputElement)" />
        <Modal v-if="showingUpload && canUpload" :title="`Upload to ${lib.name}`" @close="showingUpload = false">
          <div class="space-y-4" data-testid="upload-panel">
            <div class="grid gap-2 sm:grid-cols-2">
              <button class="btn-primary h-12" data-testid="upload-files-button" @click="fileInput?.click()">Choose files</button>
              <button class="btn-outline h-12" data-testid="upload-folder-button" @click="folderInput?.click()">Choose a folder</button>
            </div>
            <p class="mono text-text-muted">
              Any files, encrypted on this device before they leave it. Or drop them on the library.
            </p>
            <div v-if="lib.kind === 'files'">
              <label class="label" for="upload-copies">Playback copies</label>
              <select id="upload-copies" v-model="uploadCopies" class="input" data-testid="upload-copies">
                <option v-for="m in COPY_MODES" :key="m.mode" :value="m.mode">{{ m.label }}</option>
              </select>
              <p class="mono mt-1 text-text-muted">{{ copyHint(uploadCopies) }}. Originals are always kept as uploaded.</p>
            </div>
            <p v-if="!lib.can_manage" class="mono text-text-muted" data-testid="contributor-note">You can upload here, and edit or delete what you add.</p>
          </div>
        </Modal>
        <!-- Which copy this device streams -->
        <Modal v-if="showingStream" title="Playback on this device" @close="showingStream = false">
          <div class="-mx-5 -my-5" role="radiogroup" aria-label="Stream" data-testid="stream-panel">
            <label
              v-for="o in STREAM_OPTIONS"
              :key="o.value"
              class="flex min-h-14 cursor-pointer items-start gap-3 border-b border-border px-5 py-3 last:border-b-0 hover:bg-bg-hover"
            >
              <input v-model="stream" type="radio" name="stream-choice" :value="o.value" class="mt-1" :data-testid="`stream-choice-${o.value}`" />
              <span>
                <span class="block text-sm">{{ o.label }}</span>
                <span class="mono block text-text-muted">{{ o.hint }}</span>
              </span>
            </label>
          </div>
        </Modal>
        <section v-if="interrupted.length || jobs.length" class="border-b border-border" data-testid="library-admin">
          <!-- Uploads cut short by a reload -->
          <div v-if="interrupted.length" class="border-t border-border px-6 py-3" data-testid="interrupted-uploads">
            <div class="flex flex-wrap items-center gap-3">
              <p class="min-w-0 flex-1 text-sm">
                {{ interrupted.length }} upload{{ interrupted.length === 1 ? ' was' : 's were' }} interrupted. Pick the same
                file{{ interrupted.length === 1 ? '' : 's' }} again to carry on where {{ interrupted.length === 1 ? 'it' : 'they' }} stopped.
              </p>
              <button class="btn-ghost" data-testid="interrupted-discard" @click="discardInterrupted">Discard</button>
            </div>
            <ul class="mono mt-1 text-text-muted">
              <li v-for="p in interrupted.slice(0, 5)" :key="p.entry" class="truncate">{{ p.path }} · {{ formatBytes(p.size) }}</li>
              <li v-if="interrupted.length > 5">and {{ interrupted.length - 5 }} more</li>
            </ul>
          </div>

          <!-- Upload queue -->
          <div v-if="jobs.length" class="border-t border-border px-6 py-4" data-testid="upload-queue">
            <div class="mb-2 flex items-center gap-4">
              <h2 class="label mb-0 flex-1">
                Uploads · {{ pad(totals.done) }}/{{ pad(totals.count) }} · {{ formatBytes(totals.size) }}
              </h2>
              <button v-if="pending.length < jobs.length" class="mono text-text-muted uppercase hover:text-text-primary" @click="clearFinished">
                Clear finished
              </button>
            </div>
            <div class="mb-3 h-px bg-border">
              <div class="h-full bg-ink transition-all" :style="{ width: `${totals.fraction * 100}%` }" />
            </div>
            <ul class="scroll-thin max-h-64 overflow-y-auto">
              <li
                v-for="j in jobs"
                :key="j.key"
                class="mono grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 border-b border-border py-1.5 last:border-0"
                :data-testid="`upload-${j.state}`"
              >
                <span class="truncate text-text-secondary" :title="j.error ?? j.path">{{ j.path }}</span>
                <span class="text-text-disabled">{{ formatBytes(j.size) }}</span>
                <span
                  class="w-20 text-right uppercase"
                  :class="j.state === 'error' ? 'text-signal' : j.state === 'done' ? 'text-text-primary' : 'text-text-muted'"
                  :title="j.error"
                >
                  {{
                    j.state === 'uploading'
                      ? j.converting !== undefined
                        ? `conv ${pad(Math.floor(j.converting * 100))}%`
                        : `${pad(Math.floor(j.progress * 100))}%`
                      : j.state === 'error'
                        ? 'failed'
                        : j.state
                  }}
                </span>
                <button
                  class="w-4 text-text-muted hover:text-text-primary disabled:invisible"
                  :disabled="j.state !== 'queued' && j.state !== 'uploading'"
                  :aria-label="`Cancel ${j.path}`"
                  @click="cancel(j)"
                >
                  ✕
                </button>
              </li>
            </ul>
          </div>
        </section>

        <!-- Drop target over the whole collection while dragging -->
        <div
          v-if="dragging"
          class="pointer-events-none absolute inset-3 z-20 flex items-center justify-center border border-dashed border-ink bg-bg-primary/90"
          data-testid="upload-drop"
        >
          <span class="mono tracking-[0.2em] text-text-primary uppercase">Drop files or folders into {{ lib.name }}</span>
        </div>

        <RepoBrowser v-if="lib.kind === 'git'" :library="lib" :clone-url="cloneUrl" />
        <EncryptedEntries
          v-if="lib.kind === 'files'"
          :library="lib"
          :refresh="refreshTick"
          :query="query"
          @empty="noFiles = $event"
          @share="sharing = $event"
          @party="partying = $event"
          @search="query = $event"
        />
        <div v-if="lib.kind !== 'git' && noFiles" class="p-6">
          <div
            v-if="canUpload && !query"
            class="field-grid flex h-48 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-border-strong"
            @click="fileInput?.click()"
          >
            <span class="mono tracking-[0.2em] text-text-primary uppercase">Drop files or folders here</span>
            <span class="mono text-text-muted">any files · encrypted on this device</span>
          </div>
          <p v-else class="mono text-text-muted">Nothing here{{ query ? ' matches' : '' }}.</p>
        </div>

      </template>
    </section>

    <ShareToChat v-if="sharing" :item="sharing" @close="sharing = null" />
    <ShareToChat v-if="partying" mode="party" :item="partying.item" :queue="partying.queue" @close="partying = null" />
    <PromptModal
      v-if="creating"
      title="New library"
      label="Name"
      placeholder="e.g. Vinyl rips"
      submit-label="Create"
      :maxlength="48"
      :action="createLibrary"
      :blocked="locations.length === 0"
      @close="creating = false"
    >
      <div v-if="locations.length">
        <label class="label" for="new-library-location">Stored in</label>
        <select id="new-library-location" v-model="newLocation" class="input" data-testid="new-library-location">
          <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.label }}</option>
        </select>
      </div>
      <!-- Nowhere to put it yet: say where to get storage instead of an empty dropdown. -->
      <p v-else class="mono text-text-muted" data-testid="no-storage">
        {{ noStorage }}
        <button v-if="session.canLinkStorage" type="button" class="underline hover:text-text-primary" data-testid="attach-storage" @click="attachStorage">Attach one</button>
      </p>
    </PromptModal>
    <PromptModal
      v-if="creatingRepo"
      title="New repository"
      label="Name"
      placeholder="e.g. website"
      submit-label="Create"
      :maxlength="48"
      :action="createRepository"
      :blocked="locations.length === 0"
      @close="creatingRepo = false"
    >
      <div v-if="locations.length">
        <label class="label" for="new-library-location">Stored in</label>
        <select id="new-library-location" v-model="newLocation" class="input" data-testid="new-library-location">
          <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.label }}</option>
        </select>
      </div>
      <!-- Nowhere to put it yet: say where to get storage instead of an empty dropdown. -->
      <p v-else class="mono text-text-muted" data-testid="no-storage">
        {{ noStorage }}
        <button v-if="session.canLinkStorage" type="button" class="underline hover:text-text-primary" data-testid="attach-storage" @click="attachStorage">Attach one</button>
      </p>
    </PromptModal>
    <PromptModal
      v-if="renaming && lib"
      :title="lib.kind === 'git' ? 'Rename repository' : 'Rename library'"
      label="Name"
      :initial="lib.name"
      submit-label="Rename"
      :maxlength="48"
      :action="renameLibrary"
      @close="renaming = false"
    />
    <PromptModal
      v-if="deleting && lib"
      :title="lib.kind === 'git' ? 'Delete repository' : 'Delete library'"
      :message="deleteMessage"
      :label="`Type ${lib.name} to confirm`"
      :confirm="lib.name"
      submit-label="Delete"
      :maxlength="48"
      :action="deleteLibrary"
      @close="deleting = false"
    />
  </main>
</template>
